/**
 * AssistantFeedback — the thumbs under an answer (mission 8 WP08 T041; FR-011, SAT.8, FR-025; OR-13;
 * sheet 22 callout 7).
 *
 * Two ghost buttons: 24 px glyphs in 32 px targets (`size-8`, OR-13), each with its register label
 * (`assistantFeedbackUp`, `assistantFeedbackDown`) and `aria-pressed` for the saved verdict. Each click
 * is one PUT through `useFeedbackMutation`, which writes the verdict into the run's cached pages; a
 * refused PUT shows `assistantFeedbackFailed` and keeps the verdict that was saved. The comment field
 * and the response-mode note are not built (C-008).
 *
 * Mission 9 WP14 T090 (amendment 8, DM-01M3N29ND4SSNKVQAEH8WVDNRF; FR-015; B.1; U11): while
 * `useBillingGate` refuses (read-only, pending) each thumb is disabled with the gate's reason; the saved
 * verdict still shows.
 */
import { useState } from 'react'
import { ThumbsDown, ThumbsUp, type LucideIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import type { AssistantVerdict } from '@/lib/api/assistant'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { copyText } from '@/lib/provisional-copy'
import { useFeedbackMutation } from '@/lib/queries'

const THUMBS: ReadonlyArray<{ verdict: AssistantVerdict; icon: LucideIcon; label: 'assistantFeedbackUp' | 'assistantFeedbackDown' }> = [
  { verdict: 'up', icon: ThumbsUp, label: 'assistantFeedbackUp' },
  { verdict: 'down', icon: ThumbsDown, label: 'assistantFeedbackDown' },
]

export interface AssistantFeedbackProps {
  runId: string
  messageId: string
  /** The verdict the conversation read returned. */
  verdict: AssistantVerdict | null
}

export function AssistantFeedback({ runId, messageId, verdict }: AssistantFeedbackProps) {
  const feedback = useFeedbackMutation(runId)
  const gate = useBillingGate()
  const [saved, setSaved] = useState<AssistantVerdict | null>(null)
  const current = saved ?? verdict

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        {THUMBS.map(({ verdict: v, icon: Icon, label }) => (
          <Button
            key={v}
            variant="ghost"
            size="icon"
            className="size-8 [&_svg]:size-6 aria-pressed:bg-navy-100 aria-pressed:text-navy-950"
            aria-label={copyText(label)}
            aria-pressed={current === v}
            {...gateButton(gate)}
            onClick={() =>
              feedback.mutate({ messageId, verdict: v }, { onSuccess: (result) => setSaved(result.verdict) })
            }
          >
            <Icon aria-hidden />
          </Button>
        ))}
      </div>
      {feedback.isError ? (
        <p role="alert" className="text-caption text-[var(--color-error-700)]">
          {copyText('assistantFeedbackFailed')}
        </p>
      ) : null}
    </div>
  )
}
